import * as ProgressPrimitive from '@radix-ui/react-progress'
import * as React from 'react'

import { cn } from '#app/utils/cn.ts'
interface ProgressProps
	extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
	variant?: 'default' | 'warning' | 'destructive'
	animateOnMount?: boolean
}

const Progress = React.forwardRef<
	React.ComponentRef<typeof ProgressPrimitive.Root>,
	ProgressProps
>(
	(
		{ className, value, variant = 'default', animateOnMount = false, ...props },
		ref,
	) => {
		const [isInitialRender, setIsInitialRender] = React.useState(animateOnMount)

		React.useEffect(() => {
			if (!animateOnMount) return
			// Small delay before starting animation for better visual effect
			const timer = setTimeout(() => setIsInitialRender(false), 50)
			return () => clearTimeout(timer)
		}, [animateOnMount])

		const displayValue = isInitialRender ? 0 : value || 0

		const variantClasses = {
			default: 'bg-good',
			warning: 'bg-needsImprovement',
			destructive: 'bg-poor',
		}

		return (
			<ProgressPrimitive.Root
				ref={ref}
				className={cn(
					'relative h-2.5 w-full overflow-hidden rounded-full bg-black/[0.05] shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] dark:bg-white/[0.05] dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)]',
					className,
				)}
				value={value}
				{...props}
			>
				<ProgressPrimitive.Indicator
					className={cn(
						'h-full w-full flex-1 rounded-full shadow-sm',
						variantClasses[variant],
					)}
					style={{
						transform: `translateX(-${100 - displayValue}%)`,
						transition: 'transform 1000ms cubic-bezier(0.65, 0, 0.35, 1)',
					}}
				/>
			</ProgressPrimitive.Root>
		)
	},
)
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
